/**
 * The ratio as one quiet chip in the bar, and the pill row it unfolds (board `ColumnFurniture`,
 * frames 1–4: "the chip is a door, not a row"). The bar holds three things — direction field · chip
 * · Generate — and every per-generate tool that comes after joins it the same way, so this is the
 * shape they copy.
 *
 * Nothing about the ratio's meaning changes here: the chip states the effective ratio (the user's
 * pick, or the shape of the take on screen — `chains.effectiveRatio`), a pick rides the next
 * Generate and re-fits the frame under it, and the pick survives the sign-in return token. Only the
 * row's permanence is gone.
 *
 * Two components rather than one because they stand in two places: the chip inside the bar, the row
 * wherever the HOST decides to stand it — not this file's decision. The sheet, bottom-anchored,
 * still overlays the row above the bar in the flow (frame 4), `Sheet.tsx`'s own scope. The column
 * tried that too (plan `2026_09_18_armed_frame_budget.md` § D4: an absolutely positioned overlay
 * inside its own dock) and moved on the same day (dev look, 2026-09-18): surfaceless pills sitting
 * on the picture's lower edge read as belonging to nothing, and landed exactly where the counter's
 * own new corner pill stands (`Counter.tsx`'s `layout="split"`). `Column.tsx` now unfolds the row IN
 * the bar's own slot instead — the field, the ratio chip and (Ovid) the pencil and identity chip all
 * make way for it, the same grammar the muse strip already uses for its own full-width turn.
 *
 * `open` is the host's state, not theirs: Escape must fold the row WITHOUT closing the column, and
 * the column's Escape arrives through its focus trap — a document listener in the capture phase,
 * which no handler inside can outrun. So the host owns the flag and answers Escape with it: one
 * layer at a time, the innermost first, the same rule the sign-in card follows.
 */
import {useEffect, useRef} from 'react'
import type {RatioKey} from '@core/wall/contract'
import styles from './RatioFold.module.scss'

export type RatioRowProps = {
    id: string
    keys: readonly RatioKey[]
    /** The effective ratio — which pill is lit. */
    ratio: RatioKey
    onPick: (ratio: RatioKey) => void
    /** The host's box: how wide the row is and where under the bar it aligns. */
    className: string
}

export function RatioRow({id, keys, ratio, onPick, className}: RatioRowProps) {
    // The row is rendered only while open, so mounting IS unfolding: focus goes to the lit pill.
    const ref = useRef<HTMLDivElement>(null)
    useEffect(() => {
        ref.current?.querySelector<HTMLElement>('[data-current="true"]')?.focus()
    }, [])

    return (
        <div
            id={id}
            ref={ref}
            className={`${styles.row} ${className}`}
            role="group"
            aria-label="Ratio"
            data-wall-return-exempt="true"
        >
            {keys.map((key) => (
                <button
                    key={key}
                    type="button"
                    className={key === ratio ? `${styles.pill} ${styles.pillOn}` : styles.pill}
                    data-current={key === ratio}
                    aria-pressed={key === ratio}
                    onClick={() => onPick(key)}
                >
                    {key}
                </button>
            ))}
        </div>
    )
}

export type RatioChipProps = {
    rowId: string
    ratio: RatioKey
    open: boolean
    onToggle: () => void
}

export function RatioChip({rowId, ratio, open, onToggle}: RatioChipProps) {
    // Folding returns the keyboard to the door that opened the row — whether it folded by Escape,
    // by a pill, or by the chip itself.
    const ref = useRef<HTMLButtonElement>(null)
    const wasOpen = useRef(open)
    useEffect(() => {
        if (open === wasOpen.current) return
        wasOpen.current = open
        if (!open) ref.current?.focus()
    }, [open])

    return (
        <button
            type="button"
            ref={ref}
            className={open ? `${styles.chip} ${styles.chipOpen}` : styles.chip}
            aria-expanded={open}
            aria-controls={open ? rowId : undefined}
            aria-label={`Ratio: ${ratio}`}
            // A host that unmounts this chip while the row is open (`Column.tsx`, this round) cannot
            // rely on the `open`-transition effect above to hand focus back on close — mounting IS
            // that transition then, and the effect never sees it run. `data-ratio-chip` is what such
            // a host queries for instead, the same imperative-lookup idiom `RatioRow`'s own mount
            // effect already uses (`[data-current="true"]`) for the identical reason.
            data-ratio-chip="true"
            onClick={onToggle}
        >
            {ratio}
        </button>
    )
}
